import { useEffect } from "react";
import { Key,GitBranch } from "lucide-react";
import { useFormContext } from "react-hook-form";
import FormField from "./ui/FormField";
import Input from "./ui/Input";
import PasswordInput from "./ui/PasswordInput";
import Select from "./ui/Select";
import { LIFECYCLE_PLATFORM, VCS_PLATFORM, VCS_PLATFORM_DROPDOWN_OPTIONS } from "../utils/constants";

const VersionControlConfig = () => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const selectedVcsPlatform = watch("diff_adapter_type");

  // Watch ALM fields to trigger autofill for VCS fields
  const selectedWorkItemPlatform = watch("work_item_adapter_type");
  const almOrg = watch("azure_devops_org");
  const almProj = watch("azure_devops_project");

  // Autofill Logic: Syncs ALM Org/Project down to VCS Org/Project
  useEffect(() => {
    if (
      selectedVcsPlatform === VCS_PLATFORM.AZURE_DEVOPS_GIT &&
      selectedWorkItemPlatform === LIFECYCLE_PLATFORM.AZURE_DEVOPS
    ) {
      setValue("version_control_azure_devops_org", almOrg || "", {
        shouldValidate: true,
      });
      setValue("version_control_azure_devops_project", almProj || "", {
        shouldValidate: true,
      });
    }
  }, [
    almOrg,
    almProj,
    selectedVcsPlatform,
    selectedWorkItemPlatform,
    setValue,
  ]);

  return (
    <div className="w-full! border! border-slate-200! rounded-3xl! p-8! mb-5! bg-slate-50/50!">
      {/* Section Header */}
      <div className="flex items-center gap-3! mb-5! pb-4! border-b! border-slate-200!">
        <div className="bg-teal-100! p-2.5! rounded-xl!">
          <GitBranch className="text-teal-600!" size={20} />
        </div>
        <div>
          <h4 className="text-lg font-bold! text-slate-800! m-0!">
            Version Control Configuration
          </h4>
          <p className="text-sm text-slate-500! m-0! mt-0.5!">
            Configure your codebase repository and target diffs.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8! w-full!">
        <FormField
          label="Version Control Platform"
          error={errors.diff_adapter_type}
          required
        >
          <Select
            {...register("diff_adapter_type", {
              required: "Platform is required",
            })}
            options={VCS_PLATFORM_DROPDOWN_OPTIONS}
          />
        </FormField>

        {/* CONDITIONALLY RENDERED: Version Control PAT */}
        {selectedVcsPlatform && selectedVcsPlatform !== "" && (
          <FormField
            label="Personal Access Token (PAT) / API Key"
            icon={Key}
            error={errors.diff_adapter_pat}
            required
          >
            <PasswordInput
              {...register("diff_adapter_pat", {
                required: selectedVcsPlatform
                  ? "Version Control Platform's PAT is required"
                  : false,
              })}
              placeholder="Enter your access token"
            />
          </FormField>
        )}
      </div>

      {/* CONDITIONALLY RENDERED: Version Control Azure DevOps Org and Project Row */}
      {selectedVcsPlatform === VCS_PLATFORM.AZURE_DEVOPS_GIT && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8! w-full!">
          <FormField
            label="Azure DevOps Organization (VCS)"
            error={errors.version_control_azure_devops_org}
            required
          >
            <Input
              {...register("version_control_azure_devops_org", {
                required:
                  selectedVcsPlatform === VCS_PLATFORM.AZURE_DEVOPS_GIT
                    ? "Organization is required"
                    : false,
              })}
              placeholder="e.g. my-org"
            />
          </FormField>
          <FormField
            label="Azure DevOps Project (VCS)"
            error={errors.version_control_azure_devops_project}
            required
          >
            <Input
              {...register("version_control_azure_devops_project", {
                required:
                  selectedVcsPlatform === VCS_PLATFORM.AZURE_DEVOPS_GIT
                    ? "Project is required"
                    : false,
              })}
              placeholder="e.g. my-project"
            />
          </FormField>
        </div>
      )}

      {/* CONDITIONALLY RENDERED: Bitbucket Email */}
      {selectedVcsPlatform === VCS_PLATFORM.BITBUCKET && (
        <FormField
          label="Bitbucket Email"
          error={errors.bitbucket_email}
          required
        >
          <Input
            {...register("bitbucket_email", {
              required:
                selectedVcsPlatform === VCS_PLATFORM.BITBUCKET
                  ? "Email is required"
                  : false,
            })}
            placeholder="e.g. user@example.com"
          />
        </FormField>
      )}

      <FormField
        label="Repository / Instance URL"
        error={errors.repository}
        required
      >
        <Input
          {...register("repository", { required: "Repository is required" })}
          placeholder="e.g. owner/repo"
        />
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8! w-full!">
        <FormField label="Base Version" error={errors.source_version} required>
          <Input
            {...register("source_version", {
              required: "Base Version is required",
            })}
            placeholder="e.g. main"
          />
        </FormField>
        <FormField
          label="Target Version"
          error={errors.target_version}
          required
        >
          <Input
            {...register("target_version", {
              required: "Target Version is required",
            })}
            placeholder="e.g. dev"
          />
        </FormField>
      </div>
    </div>
  );
};

export default VersionControlConfig;
